import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Premium Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/**
 * PythonOOPCore Component
 * * Teaches the three pillars of OOP: Inheritance, Polymorphism, and Encapsulation.
 * Optimized for junior developers with clear comments and modular data.
 */
export default function PythonOOPCore() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* ✅ State Management */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    /* ✅ Handle Scroll for "Back to Top" button */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    /* ✅ Accessibility: Text-to-Speech */
    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) {
            setIsSpeaking(false);
            return;
        }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.95;
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const stopSpeech = () => {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
    };

    /* ✅ Utility: Clipboard Copy */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ✅ Core Concept Data */
    const oopConcepts = useMemo(() => [
        {
            title: "1. Inheritance",
            tag: "Code Reusability",
            desc: "Inheritance allows a 'child' class to derive attributes and methods from a 'parent' class. This promotes a hierarchy and prevents code duplication.",
            code: `class Animal:\n    def speak(self):\n        print("Some generic sound")\n\nclass Dog(Animal):\n    def wag_tail(self):\n        print("Wagging...")\n\nd = Dog()\nd.speak()     # Inherited from Animal\nd.wag_tail()  # Specific to Dog`
        },
        {
            title: "2. Polymorphism",
            tag: "Multiple Forms",
            desc: "Polymorphism lets different classes be treated as instances of the same general class through the same interface. One method name performs different actions based on the object.",
            code: `class Bird:\n    def speak(self):\n        print("Tweet")\n\nclass Cat:\n    def speak(self):\n        print("Meow")\n\nfor animal in [Bird(), Cat()]:\n    animal.speak()  # One call, different results`
        },
        {
            title: "3. Encapsulation",
            tag: "Data Privacy",
            desc: "Encapsulation wraps data and methods into a single unit and restricts direct access to some components using private attributes (double underscores '__').",
            code: `class BankAccount:\n    def __init__(self, balance):\n        self.__balance = balance  # Private attribute\n\n    def get_balance(self):\n        return self.__balance\n\nacc = BankAccount(100)\nprint(acc.get_balance())  # 100\n# print(acc.__balance)    # Throws AttributeError`
        }
    ], []);

    return (
        <PageWrapper>
            {/* 🏆 Section Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h4" : "h3"}
                    sx={{
                        fontWeight: 800,
                        backgroundColor: "goldenrod",
                        color: "#100720",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    PYTHON OOP: CORE
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 🎯 Pillars Introduction */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                Pillars of OOP
                            </Typography>
                            <IconButton
                                onClick={() => handleSpeak("Inheritance, Polymorphism, and Encapsulation are the fundamental pillars of object-oriented programming. They make your code flexible and secure.")}
                                sx={{ color: "goldenrod" }}
                            >
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
                            These concepts allow you to build complex systems by creating classes that are extendable, protect internal logic from external interference, and behave differently depending on their context.
                        </Typography>

                        <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
                            <Typography variant="caption" sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", p: 1, borderRadius: 1, color: "goldenrod" }}>
                                • Secure Data
                            </Typography>
                            <Typography variant="caption" sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", p: 1, borderRadius: 1, color: "goldenrod" }}>
                                • Reusable Logic
                            </Typography>
                            <Typography variant="caption" sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", p: 1, borderRadius: 1, color: "goldenrod" }}>
                                • Scalable Design
                            </Typography>
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Iterating Through Concept Cards */}
                {oopConcepts.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiFillBulb size={24} />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>
                                            {item.title}
                                        </Typography>
                                        <Typography variant="caption" color="goldenrod" fontWeight={800}>
                                            {item.tag}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title="Listen">
                                        <IconButton onClick={() => handleSpeak(`${item.title}. ${item.desc}`)} sx={{ color: "goldenrod" }}>
                                            <VolumeUpIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{ color: "goldenrod", borderColor: "goldenrod" }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}>
                                {item.desc}
                            </Typography>

                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Navigation Footer */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Link to="/pythonCodingDashBoard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Utility FABs */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={stopSpeech}>
                            <StopIcon />
                        </Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab
                        size="small"
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        sx={{ bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                    >
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
